import {graphql} from 'gatsby'
import React, {FC, useState} from 'react'

import {PricingFragment} from '@/graphql-types'
import {useEmailValidationErrorMessage} from '@/hooks/use-email-validation-error-message'
import useSignup from '@/hooks/use-signup'
import pinkBadge from '@/images/icons/pink-badge.svg'
import {wait} from '@/utils/wait'
import Button from '@helios/shared/components/button'
import Input from '@helios/shared/components/input'
import {Modal, ModalContent, ModalActions} from '@helios/shared/components/modal'
import useNavigate from '@helios/shared/hooks/use-navigate'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {Text} from '@helios/shared/styles/typography'
import ImageSource from '@helios/shared/types/image'
import {track, TrackingEvent} from '@helios/shared/utils/analytics'
import hasNonEmptyItems from '@helios/shared/utils/has-non-empty-items'

import * as s from './pricing.styles'

export interface Props {
    withCtas?: boolean
}

interface PricingProps extends Props {
    content: PricingFragment['data']
}

enum Tier {
    Free = 'free',
    Pro = 'pro',
    Premium = 'premium',
}

export const Pricing: FC<PricingProps> = ({content, withCtas = true}) => {
    const [activeItemIndex, setActiveItemIndex] = useState(0)
    const [activeSignupModal, setActiveSignupModal] = React.useState<Tier | null>(null)
    const tiers = content.pricing_tiers.map(({pricing_tier}) => pricing_tier)
    const navigate = useNavigate()

    const getTierName = (index: number): Tier => {
        switch (index) {
            case 0:
                return Tier.Free

            case 1:
                return Tier.Pro

            case 2:
                return Tier.Premium

            default:
                throw new Error('Unrecognized tier')
        }
    }

    const handlePremiumTierClick = async () => {
        track(TrackingEvent.PlanSelected, {
            value: Tier.Premium,
        })

        await wait(300)

        navigate('/demo')
    }

    return (
        <s.OuterWrapper>
            {!!content.promo_badge && (
                <s.BadgeWrapper>
                    <s.Badge src={pinkBadge} alt="" />

                    <s.BadgeText size="xxs" font="secondary">
                        {content.promo_badge}
                    </s.BadgeText>
                </s.BadgeWrapper>
            )}

            <s.Wrapper activeItemIndex={activeItemIndex}>
                <s.Tabs>
                    {tiers.map((tier, i) => (
                        <s.Tab
                            key={tier.document.data.name}
                            isActive={i === activeItemIndex}
                            onClick={() => setActiveItemIndex(i)}
                        >
                            <Text as="span" center>
                                {tier.document.data.name}
                            </Text>
                        </s.Tab>
                    ))}
                </s.Tabs>

                <s.Title>
                    {tiers.map((tier) => (
                        <s.TiersTitle key={tier.document.data.name}>
                            <Text as="h3" size="lg">
                                {tier.document.data.name}
                            </Text>
                            {tier.document.data.new && (
                                <s.NewTierBadge>{tier.document.data.new}</s.NewTierBadge>
                            )}
                        </s.TiersTitle>
                    ))}
                </s.Title>

                <s.MobileNewTierBadge>
                    {tiers.map((tier) => (
                        <s.Box key={tier.document.data.name}>
                            {tier.document.data.new && (
                                <s.NewTierBadge>{tier.document.data.new}</s.NewTierBadge>
                            )}
                        </s.Box>
                    ))}
                </s.MobileNewTierBadge>

                <s.Excerpt>
                    {tiers.map((tier) => (
                        <s.Box key={tier.document.data.name}>
                            <Text>{tier.document.data.excerpt}</Text>
                        </s.Box>
                    ))}
                </s.Excerpt>

                <s.Amount withCtas={withCtas}>
                    {tiers.map((tier) => (
                        <s.Box key={tier.document.data.name}>
                            {Number.isInteger(tier.document.data.amount) &&
                                tier.document.data.currency && (
                                    <Text color="muted" weight="semiBold">
                                        {tier.document.data.amount} {tier.document.data.currency}
                                    </Text>
                                )}

                            <Text size="xs" color="lightMuted" font="secondary">
                                {tier.document.data.amount_subtext}
                            </Text>
                        </s.Box>
                    ))}
                </s.Amount>

                {withCtas && (
                    <s.Cta>
                        {tiers.map((tier, i) => (
                            <s.Box key={tier.document.data.name}>
                                {i === tiers.length - 1 ? (
                                    <Button
                                        onClick={handlePremiumTierClick}
                                        variant={!(i % 2) ? 'outline' : 'default'}
                                    >
                                        {tier.document.data.cta_text}
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={() => {
                                            track(TrackingEvent.PlanSelected, {
                                                value: getTierName(i),
                                            })

                                            setActiveSignupModal(getTierName(i))
                                        }}
                                        variant={!(i % 2) ? 'outline' : 'default'}
                                    >
                                        {tier.document.data.cta_text}
                                    </Button>
                                )}
                            </s.Box>
                        ))}
                    </s.Cta>
                )}

                {hasNonEmptyItems(content.signup_modal_free) && (
                    <SignupModal
                        {...content.signup_modal_free[0]}
                        isOpen={activeSignupModal === Tier.Free}
                        onDismiss={() => setActiveSignupModal(null)}
                        tier={Tier.Free}
                    />
                )}

                {hasNonEmptyItems(content.signup_modal_pro) && (
                    <SignupModal
                        {...content.signup_modal_pro[0]}
                        isOpen={activeSignupModal === Tier.Pro}
                        onDismiss={() => setActiveSignupModal(null)}
                        tier={Tier.Pro}
                    />
                )}

                <s.FeaturesTitle>
                    {tiers.map((tier) => (
                        <s.Box key={tier.document.data.name}>
                            <Text>{tier.document.data.features_title}</Text>
                        </s.Box>
                    ))}
                </s.FeaturesTitle>

                <s.Row>
                    {tiers.map((tier) => (
                        <s.Box key={tier.document.data.name}>
                            <s.FeaturesList>
                                {tier.document.data.features.map(({feature}) => (
                                    <li key={feature}>{feature}</li>
                                ))}
                            </s.FeaturesList>
                        </s.Box>
                    ))}
                </s.Row>
            </s.Wrapper>
        </s.OuterWrapper>
    )
}

type SignupModalProps = {
    isOpen: boolean
    onDismiss: () => void
    title: string
    subtitle: string
    image: ImageSource
    tier: Tier
}

const SignupModal = ({isOpen, onDismiss, title, subtitle, image, tier}: SignupModalProps) => {
    const globalStrings = useGlobalStrings()
    const {handleSubmit, clearError, setEmail, email, loading, error} = useSignup({
        form: 'Front',
        params: {pricing_plan: tier},
    })
    const errorMessage = useEmailValidationErrorMessage(error)

    const handleEmailChange = (value: string) => {
        clearError()
        setEmail(value)
    }

    const handleDismiss = () => {
        clearError()
        setEmail('')
        onDismiss()
    }

    return (
        <Modal
            title={title}
            subtitle={subtitle}
            image={image}
            isOpen={isOpen}
            onDismiss={handleDismiss}
        >
            <form onSubmit={handleSubmit} noValidate>
                <ModalContent>
                    <Input
                        type="email"
                        label={globalStrings.work_email}
                        value={email}
                        onChange={handleEmailChange}
                        error={errorMessage}
                        autoFocus
                    />
                </ModalContent>

                <ModalActions>
                    <Button type="submit" loading={loading}>
                        {globalStrings.submit}
                    </Button>
                </ModalActions>
            </form>
        </Modal>
    )
}

export const pricingFragment = graphql`
    fragment Pricing on PrismicPricingTiers {
        lang
        data {
            promo_badge
            signup_modal_free {
                image {
                    alt
                    copyright
                    url
                    dimensions {
                        width
                        height
                    }
                }
                title
                subtitle
            }
            signup_modal_pro {
                image {
                    alt
                    copyright
                    url
                    dimensions {
                        width
                        height
                    }
                }
                title
                subtitle
            }
            pricing_tiers {
                pricing_tier {
                    ... on PrismicLinkType {
                        document {
                            ... on PrismicPricingTier {
                                data {
                                    name
                                    isFree: is_free
                                    new
                                    excerpt
                                    amount
                                    currency
                                    amount_subtext
                                    cta_text
                                    features_title
                                    features {
                                        feature
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`

export default Pricing
